import { Injectable } from '@angular/core';

import { AppConfig } from '../app.config';
import { throwError } from 'rxjs';
import { filter, map, catchError } from 'rxjs/operators';
import { HelpersService } from './helpers.service';

import { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlerService } from './errorhandler.service'; // Add this for error feedback

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private env: any;
  private headers: any;

  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private helpersService: HelpersService,
    private errorHandler: ErrorHandlerService, // Add this for error feedback
  ) {
    this.env = this.appConfig.getEnvironmentNew();
    this.headers = this.helpersService.getNewApiHeaders();
  }

  getPackagesStripe() {
    return this.http
      .get(
        this.env.url + '/v4.0/Payments/Stripe/Products?code=' + this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  postPaymentStripe(data: any) {
    return this.http
      .post(
        this.env.url + '/v4.0/Payments/Stripe/Partners?code=' + this.env.key,
        data,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        }),
      );
  }

  getPaymentStatus(partnerID) {
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Status/${partnerID}?code=` +
          this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        }),
      );
  }

  getPaymentStatusV2(partnerID, product) {
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Status/${partnerID}/Product/${product}?code=` +
          this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        }),
      );
  }

  stripeBundlePaymentComplete(data: any) {
    return this.http
      .post(
        this.env.url + '/v4.0/Payments/Stripe/Bundle?code=' + this.env.key,
        data,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getCustomer(partnerId: number) {
    return this.http
      .get(
        this.env.url +
          '/v4.0/Portal/' +
          partnerId +
          '/Payments/Account?code=' +
          this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  getStripePortalSession(partnerId: number, callbackURL) {
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Stripe/PortalSession/${partnerId}?CallBackURL=${callbackURL}&code=` +
          this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  putPaymentDates(newPayment: Date, nextPayment: Date, partnerId: number) {
    newPayment.setHours(15);
    nextPayment.setHours(15);
    return this.http
      .put(
        this.env.url +
          '/v4.0/Portal/' +
          partnerId +
          '/Payment/Dates?code=' +
          this.env.key,
        { newPayment, nextPayment, partnerId },
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  getCheckSumPaygate(data: any) {
    const {
      version,
      paygateId,
      reference,
      amount,
      currency,
      returnUrl,
      transactionDate,
      email,
      subsStartDate,
      subsEndDate,
      subsFrequency,
      processNow,
      processNowAmount,
    } = data;
    return this.http
      .get(
        this.env.url +
          '/v4.0/Payments/Paygate/Checksum?Version=' +
          version +
          '&PaygateID=' +
          paygateId +
          '&Reference=' +
          reference +
          '&Amount=' +
          amount +
          '&Currency=' +
          currency +
          '&ReturnUrl=' +
          returnUrl +
          '&TransactionDate=' +
          transactionDate +
          '&Email=' +
          email +
          '&SubsStartDate=' +
          subsStartDate +
          '&SubsEndDate=' +
          subsEndDate +
          '&SubsFrequency=' +
          subsFrequency +
          '&ProcessNow=' +
          processNow +
          '&ProcessNowAmount=' +
          processNowAmount +
          '&code=' +
          this.env.key,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  postCancelSubscription(id: number) {
    return this.http
      .post(
        this.env.url +
          '/v4.0/Portal/' +
          id +
          '/Payments/Cancel?code=' +
          this.env.key,
        {},
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  getCheckSumPaygateImproved(data: any) {
    const headers = this.helpersService.getNewApiHeaders();

    let params = new URLSearchParams();
    for (let key in data) {
      params.set(key, data[key]);
    }

    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Paygate/Checksum?${params}&code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  initilizePaygatePayment(data: any) {
    let formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    var headers = {
      Authorization: `Bearer ${this.helpersService.getAuthDataNew().token}`,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/Paygate/Initiate.Trans?code=${this.env.key}`,
        formData,
        { headers: headers },
      )
      .pipe(
        map(
          (res: any) => res.text(),
          catchError((res: any) => {
            return throwError(res);
          }),
        ),
      );
  }

  processPaygatePayment(data: string) {
    var paygateForm = document.createElement('form');
    paygateForm.target = 'paygateForm';
    paygateForm.method = 'POST';
    paygateForm.action = 'https://secure.paygate.co.za/payweb3/process.trans';

    var fields = data.split('&');
    fields.forEach((field) => {
      var pair = field.split('=');
      var paygateInput = document.createElement('input');
      paygateInput.type = 'text';
      paygateInput.name = pair[0];
      paygateInput.value = pair[1];
      paygateInput.hidden = true;
      paygateForm.appendChild(paygateInput);
      document.body.appendChild(paygateForm);
    });

    var w = window.open(
      '',
      'paygateForm',
      'toolbar=0,scrollbars=0,location=0,statusbar=0,menubar=0,resizable=0,width=1024,height=768',
    );
    paygateForm.submit();
  }

  initializeHubtigerPayment(data: any) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url +
          `/v4.0/Messaging/Partner/BundlePurchase?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getPaymentRequestStatus(PayRequestID: string) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Messaging/Partner/BundlePurchaseStatus/${PayRequestID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  launchStripeConnectPopup(url: string) {
    var w = window.open(
      url,
      'stripeConnectForm',
      'toolbar=0,scrollbars=0,location=0,statusbar=0,menubar=0,resizable=0,width=860,height=890',
    );
    return w;
  }

  getStripeClientID() {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/StripeConnect/ClientId?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  checkPartnerLinkedStripeConnect(PartnerID) {
    if (PartnerID == null || PartnerID == undefined) {
      return throwError('Partner not set');
    }

    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/StripeConnect/PartnerLinked/${PartnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  linkStripeAccountToPartner(data) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/LinkAccountToPartner?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  disconnectPartnerFromStripeConnect(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .delete(
        this.env.url +
          `/v4.0/Payments/StripeConnect/Disconnect/${PartnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  initiateStripeConnectPayment(ServiceID) {
    const headers = this.helpersService.getNewApiHeaders();

    var data = {
      ServiceID: ServiceID,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/CreatePaymentIntent?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  initiateStripeConnectPayments(ServiceID) {
    const headers = this.helpersService.getNewApiHeaders();

    var data = {
      ServiceID: ServiceID,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/CreatePaymentIntentsV4?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  updateInvoiceWithStripePaymentIntent(ServiceID, PaymentIntentId) {
    const headers = this.helpersService.getNewApiHeaders();

    var data = {
      ServiceID: ServiceID,
      PaymentIntentId: PaymentIntentId,
    };
    console.log(data);
    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/PaymentReceived?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  updateInvoiceWithStripePaymentIntents(ServiceID, PaymentIntentId) {
    const headers = this.helpersService.getNewApiHeaders();

    var data = {
      ServiceID: ServiceID,
      PaymentIntentId: PaymentIntentId,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/PaymentsReceivedV2?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  updateInvoiceWithStripePaymentIntentsV2(
    ServiceID,
    PaymentIntentId,
    Amount = 0,
  ) {
    const headers = this.helpersService.getNewApiHeaders();

    var data = {
      ServiceID: ServiceID,
      PaymentIntentId: PaymentIntentId,
      Amount: Amount,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/StripeConnect/PaymentsReceivedV2?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  // getPaymentKeyPeach(){
  //   return this.http.post(
  //     `https://eft.ppay.io/api/v1/payment-key`,
  //     {
  //       amount:20,
  //       merchant_reference:'testref123'
  //     },
  //       {
  //         Content-Type: "application/json"
  //       },
  //   )
  // }

  chargeGiftCard(partnerId: number, voucher: any) {
    const headers = this.helpersService.getNewApiHeaders();

    return this.http
      .post(
        this.env.url +
          '/' +
          partnerId +
          '/GiftCards/Charge?code=' +
          this.env.key,
        voucher,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res.json());
        }),
      );
  }

  giftCardBalance(PartnerID, code) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          '/' +
          PartnerID +
          '/GiftCards/' +
          code +
          '/balance?code=' +
          this.env.key,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  checkPartnersStripePaymentOptions(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/StripeConnect/GetPaymentOptions/${PartnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  createShopifyPaymentIntent(PartnerID, data: any) {
    return this.http
      .post(
        this.env.url +
          `/webhooks/shopify/payments/${PartnerID}/intent?code=` +
          this.env.key,
        data,
        this.headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  checkIfSignedUpUsingShopify(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/webhooks/shopify/signup/${PartnerID}/type?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getServiceDepositConfig(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/Bikeshop/${PartnerID}/Workshop/DepositConfiguration?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getPartnerPaystackConfig(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Paystack/PartnerLinked/${PartnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  updateServiceDepositConfig(data) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url +
          `/Bikeshop/Workshop/DepositConfiguration?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  deletePartnerPaystackConfig(PartnerID) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .delete(
        this.env.url +
          `/v4.0/Payments/Paystack/Disconnect/${PartnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  updatePartnerPaystackConfig(PartnerID, PaystackSecretKey) {
    const headers = this.helpersService.getNewApiHeaders();

    const data = {
      PartnerID: PartnerID,
      PaystackSecretKey: PaystackSecretKey,
    };

    return this.http
      .post(
        this.env.url +
          `/v4.0/Payments/Paystack/LinkAccountToPartner?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getCurrentStoreSubscription(partnerID: number) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/ProductSubscriptions/${partnerID}?code=${this.env.key}`,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  getSubscriptionProducts(partnerID: number) {
    return this.http
      .get(
        this.env.url +
          `/v4.0/Payments/Stripe/SubscriptionProducts/${partnerID}?code=${this.env.key}`,
        {},
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((res: any) => {
          return throwError(res);
        }),
      );
  }

  createStripeSubscription(data) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url + `/v4.0/Payments/Stripe/Subscribe?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(error);
        }),
      );
  }

  updateStripeSubscription(data) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .put(
        this.env.url + `/v4.0/Payments/Stripe/Update?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(error);
        }),
      );
  }

  cancelStripeSubscription(partnerID: number, data: any) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url +
          `/v4.0/Partner/${partnerID}/Stripe/Subscription/Cancel?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(error);
        }),
      );
  }

  
  createPaystackPaymentIntent(data : any) {
    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url + `/Workshop/Service/Paystack/PostPaystackInitializeTransaction?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(error);
        }),
      );
  }

  capturePaystackPayment(data: any) {

    const headers = this.helpersService.getNewApiHeaders();
    return this.http
      .post(
        this.env.url + `/v4.0/Payments/Paystack/PaymentsReceived?code=${this.env.key}`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(error);
        }),
      );
  }

  
  postProcessPayment(data: any, partnerID: string) {
   var headers = this.helpersService.getPOSApiHeaders(
     this.appConfig.getPOSEnvironment().key, partnerID, partnerID
    );
    return this.http
      .post(
        this.appConfig.getPOSEnvironment().url +
          `/invoices/payments`,
        data,
        headers,
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          return throwError(error);
        }),
      );
  }

  getPartnerPOSConfiguration(PartnerID) {
    var headers = this.helpersService.getPOSApiHeaders(
      this.appConfig.getPOSEnvironment().key, PartnerID
    );
    console.log("Headers", headers);

    return this.http
      .get(this.appConfig.getPOSEnvironment().url + `partner/pos/configuration`, headers)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          return throwError(error.json());
        }),
      );
  }
}
